<template>
  <div class="data-display-wrapper">
    <!-- 加载状态 -->
    <Spin v-if="loading" fix>
      <div class="loading-text">
        <Icon type="ios-loading" size="30" class="spin-icon-load"></Icon>
        <div>{{ langMap.dashboard_loading[lang] }}</div>
      </div>
    </Spin>
    
    <!-- 空状态 -->
    <div v-if="!loading && !hasData" class="empty-state">
      <div class="empty-icon-wrapper">
        <div class="hologram-ring"></div>
        <div class="hologram-ring delay-1"></div>
        <div class="hologram-ring delay-2"></div>
        <div class="empty-icon">📋</div>
      </div>
      <div class="empty-text">{{ langMap.empty[lang] }}</div>
      <div class="empty-subtext">{{ emptyMessage }}</div>
    </div>
    
    <!-- 数据表格 -->
    <div v-if="!loading && hasData" class="data-content">
      <div class="table-wrapper">
        <Table
          :columns="columns"
          :data="tableData"
          stripe
          border
          size="small"
          :height="tableHeight"
        >
        <template slot-scope="{ row }" slot="taskId">
          <span class="task-id-cell" :title="row.taskId">{{ row.taskId }}</span>
        </template>
        
        <template slot-scope="{ row }" slot="robotCode">
          <span class="robot-code-cell">{{ row.robotCode }}</span>
        </template>
        
        <template slot-scope="{ row }" slot="startDate">
          <span class="date-cell">{{ row.startDate }}</span>
        </template>
        
        <template slot-scope="{ row }" slot="endDate">
          <span class="date-cell">{{ row.endDate }}</span>
        </template>
        
        <template slot-scope="{ row }" slot="modules">
          <span class="modules-cell">{{ formatModules(row.modules) }}</span>
        </template>
        
        <template slot-scope="{ row }" slot="status">
          <span :class="['status-cell', `status-${row.status}`]">
            {{ formatStatus(row.status) }}
          </span>
        </template>
        
        <template slot-scope="{ row }" slot="createTime">
          <span class="time-cell">{{ formatDateTime(row.createTime) }}</span>
        </template>
        
        <template slot-scope="{ row }" slot="action">
          <div class="action-cell">
            <a 
              v-if="row.status === 'SUCCESS' && row.relativePath" 
              :href="getDownloadUrl(row.relativePath)"
              download
              class="download-btn"
            >
              <Icon type="ios-cloud-download" size="16" />
              {{ langMap.dashboard_download[lang] }}
            </a>
            <span v-else-if="row.status === 'FAIL'" class="error-msg" :title="row.failReason">
              {{ row.failReason || langMap.dashboard_exportFailed[lang] }}
            </span>
            <span v-else class="status-text">-</span>
          </div>
        </template>
        </Table>
      </div>
      
      <!-- 自定义分页器 -->
      <div class="pagination-wrapper">
        <div class="cyber-pagination">
          <!-- 总数信息 -->
          <div class="pagination-info">
            <span class="info-label">{{ langMap.dashboard_totalRecords[lang] }}</span>
            <span class="info-value">{{ pagination.total }}</span>
            <span class="info-divider">|</span>
            <span class="info-text">{{ pageRangeText }}</span>
          </div>
          
          <!-- 页码按钮组 -->
          <div class="pagination-controls">
            <!-- 首页 -->
            <button 
              class="page-btn page-first"
              :disabled="pagination.current === 1"
              @click="handlePageChange(1)"
            >
              <span class="btn-icon">《《</span>
            </button>
            
            <!-- 上一页 -->
            <button 
              class="page-btn page-prev"
              :disabled="pagination.current === 1"
              @click="handlePageChange(pagination.current - 1)"
            >
              <span class="btn-icon">‹</span>
            </button>
            
            <!-- 页码按钮 -->
            <div class="page-numbers">
              <button
                v-for="page in visiblePages"
                :key="page"
                :class="['page-number', { active: page === pagination.current, ellipsis: page === '...' }]"
                :disabled="page === '...'"
                @click="page !== '...' && handlePageChange(page)"
              >
                <span class="number-glow" v-if="page === pagination.current"></span>
                {{ page }}
              </button>
            </div>
            
            <!-- 下一页 -->
            <button 
              class="page-btn page-next"
              :disabled="pagination.current === totalPages"
              @click="handlePageChange(pagination.current + 1)"
            >
              <span class="btn-icon">›</span>
            </button>
            
            <!-- 尾页 -->
            <button 
              class="page-btn page-last"
              :disabled="pagination.current === totalPages"
              @click="handlePageChange(totalPages)"
            >
              <span class="btn-icon">》》</span>
            </button>
          </div>
          
          <!-- 每页条数选择 -->
          <div class="page-size-selector">
            <span class="selector-label">{{ langMap.dashboard_perPage[lang] }}</span>
            <div class="size-options">
              <button
                v-for="size in pageSizeOptions"
                :key="size"
                :class="['size-option', { active: pagination.size === size }]"
                @click="handlePageSizeChange(size)"
              >
                {{ size }}
              </button>
            </div>
          </div>
          
          <!-- 快速跳转 -->
          <div class="page-jumper">
            <span class="jumper-label">{{ langMap.dashboard_jumpTo[lang] }}</span>
            <input 
              type="number" 
              class="jumper-input"
              v-model.number="jumpPage"
              @keyup.enter="handleJumpPage"
              :placeholder="String(pagination.current)"
              min="1"
              :max="totalPages"
            />
            <button class="jumper-btn" @click="handleJumpPage">
              <span class="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import langMap from '@/utils/language'
import { mapState } from 'vuex'
import { getMvpUrlMap } from '@/utils/websocket'

export default {
  name: 'ModuleLogHistoryTable',
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    hasData: {
      type: Boolean,
      default: false
    },
    emptyMessage: {
      type: String,
      default: ''
    },
    tableData: {
      type: Array,
      default: () => []
    },
    pagination: {
      type: Object,
      default: () => ({
        total: 0,
        current: 1,
        size: 20,
        pages: 0
      })
    }
  },
  data() {
    return {
      langMap,
      tableHeight: 500,
      pageSizeOptions: [10, 20, 30, 50],
      jumpPage: null
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    columns() {
      return [
        {
          title: langMap.dashboard_robotCode[this.lang],
          slot: 'robotCode',
          align: 'center',
          width: 150
        },
        {
          title: langMap.dashboard_logCreateTime[this.lang],
          slot: 'createTime',
          align: 'center',
          width: 180
        },
        {
          title: langMap.dashboard_logTaskId[this.lang],
          slot: 'taskId',
          align: 'center',
          width: 200,
          ellipsis: true
        },
        {
          title: langMap.dashboard_logStartDate[this.lang],
          slot: 'startDate',
          align: 'center',
          minWidth: 100
        },
        {
          title: langMap.dashboard_logEndDate[this.lang],
          slot: 'endDate',
          align: 'center',
          minWidth: 100
        },
        {
          title: langMap.dashboard_logModules[this.lang],
          slot: 'modules',
          align: 'center',
          minWidth: 70
        },
        {
          title: langMap.dashboard_logStatus[this.lang],
          slot: 'status',
          align: 'center',
          minWidth: 120
        },
        {
          title: langMap.dashboard_operation[this.lang],
          slot: 'action',
          align: 'center',
          width: 200
        }
      ]
    },
    totalPages() {
      return this.pagination.pages || Math.ceil(this.pagination.total / this.pagination.size) || 1
    },
    pageRangeText() {
      const start = (this.pagination.current - 1) * this.pagination.size + 1
      const end = Math.min(this.pagination.current * this.pagination.size, this.pagination.total)
      return `${start}-${end}`
    },
    visiblePages() {
      const total = this.totalPages
      const current = this.pagination.current
      const delta = 2
      const range = []
      const rangeWithDots = []

      for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
        range.push(i)
      }

      if (current - delta > 2) {
        rangeWithDots.push(1, '...')
      } else {
        rangeWithDots.push(1)
      }

      rangeWithDots.push(...range)

      if (current + delta < total - 1) {
        rangeWithDots.push('...', total)
      } else if (total > 1) {
        rangeWithDots.push(total)
      }

      return rangeWithDots
    }
  },
  mounted() {
    this.calculateTableHeight()
    window.addEventListener('resize', this.calculateTableHeight)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateTableHeight)
  },
  methods: {
    // 格式化模块列表
    formatModules(modulesStr) {
      if (!modulesStr) return '-'
      try {
        const modules = JSON.parse(modulesStr)
        if (Array.isArray(modules)) {
          if (modules.includes('ALL') || modules.length === 0) {
            return this.langMap.dashboard_logAllModules[this.lang]
          }
          return modules.join(', ')
        }
        return modulesStr
      } catch (e) {
        return modulesStr
      }
    },
    
    // 格式化任务状态
    formatStatus(status) {
      const statusMap = {
        'INIT': this.langMap.dashboard_logStatusInit[this.lang],
        'UPLOADING': this.langMap.dashboard_logStatusUploading[this.lang],
        'PACKING': this.langMap.dashboard_logStatusPacking[this.lang],
        'SUCCESS': this.langMap.dashboard_logStatusSuccess[this.lang],
        'FAIL': this.langMap.dashboard_logStatusFail[this.lang]
      }
      return statusMap[status] || status || '-'
    },
    
    // 获取下载地址
    getDownloadUrl(relativePath) {
      if (!relativePath) return '#'
      // relativePath 已包含完整相对路径，统一拼接下载地址
      const sanitizedPath = String(relativePath).replace(/^\/+/, '')
      return `${getMvpUrlMap}/file/${sanitizedPath}`
    },
    
    // 格式化日期时间
    formatDateTime(timestamp) {
      if (!timestamp) return '-'
      
      // 如果已经是格式化后的字符串，直接返回
      if (typeof timestamp === 'string' && timestamp.includes('-')) {
        return timestamp
      }
      
      // 否则将时间戳转换为日期格式
      const date = new Date(timestamp)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
    },
    
    // 计算表格高度
    calculateTableHeight() {
      const windowHeight = window.innerHeight
      const headerHeight = 120
      const paginationHeight = 60
      const padding = 40
      this.tableHeight = windowHeight - headerHeight - paginationHeight - padding - 281
      if (this.tableHeight < 300) {
        this.tableHeight = 300
      }
    },
    
    // 分页处理
    handlePageChange(page) {
      this.$emit('page-change', page)
    },
    
    handlePageSizeChange(size) {
      this.$emit('size-change', size)
    },
    
    // 快速跳转页面
    handleJumpPage() {
      if (!this.jumpPage || this.jumpPage < 1 || this.jumpPage > this.totalPages) {
        this.$Message.warning(
          langMap.dashboard_inputValidPageRange[this.lang].replace('{max}', this.totalPages)
        )
        this.jumpPage = null
        return
      }
      this.handlePageChange(this.jumpPage)
      this.jumpPage = null
    }
  }
}
</script>

<style lang="less" scoped>
.data-display-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.task-id-cell {
  font-family: 'Courier New', monospace;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.robot-code-cell {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #00f0ff;
  font-weight: 600;
  text-shadow: 0 0 5px rgba(0, 240, 255, 0.4);
}

.date-cell {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
}

.modules-cell {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #00f0ff;
  font-weight: 500;
}

.time-cell {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
}

.status-cell {
  font-family: 'Courier New', monospace;
  font-weight: 700;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 4px;
  display: inline-block;
  
  &.status-INIT {
    color: #FFD700;
    background: rgba(255, 215, 0, 0.15);
    border: 1px solid rgba(255, 215, 0, 0.4);
    text-shadow: 0 0 5px rgba(255, 215, 0, 0.6);
  }
  
  &.status-UPLOADING {
    color: #00f0ff;
    background: rgba(0, 240, 255, 0.15);
    border: 1px solid rgba(0, 240, 255, 0.4);
    text-shadow: 0 0 5px rgba(0, 240, 255, 0.6);
  }
  
  &.status-PACKING {
    color: #9370DB;
    background: rgba(147, 112, 219, 0.15);
    border: 1px solid rgba(147, 112, 219, 0.4);
    text-shadow: 0 0 5px rgba(147, 112, 219, 0.6);
  }
  
  &.status-SUCCESS {
    color: #39FF14;
    background: rgba(57, 255, 20, 0.15);
    border: 1px solid rgba(57, 255, 20, 0.4);
    text-shadow: 0 0 5px rgba(57, 255, 20, 0.6);
  }
  
  &.status-FAIL {
    color: #FF4444;
    background: rgba(255, 68, 68, 0.15);
    border: 1px solid rgba(255, 68, 68, 0.4);
    text-shadow: 0 0 5px rgba(255, 68, 68, 0.6);
  }
}

.action-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  .download-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 12px;
    background: linear-gradient(135deg, rgba(0, 240, 255, 0.2) 0%, rgba(0, 200, 255, 0.2) 100%);
    border: 1px solid rgba(0, 240, 255, 0.5);
    border-radius: 4px;
    color: #00f0ff;
    font-size: 12px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.3s ease;
    cursor: pointer;
    
    &:hover {
      background: linear-gradient(135deg, rgba(0, 240, 255, 0.3) 0%, rgba(0, 200, 255, 0.3) 100%);
      box-shadow: 0 0 10px rgba(0, 240, 255, 0.5);
      transform: translateY(-1px);
    }
    
    i {
      font-size: 14px;
    }
  }
  
  .error-msg {
    font-size: 11px;
    color: #FF4444;
    max-width: 180px;
    word-break: break-word;
    white-space: normal;
    line-height: 1.4;
  }
  
  .status-text {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.5);
  }
}
</style>
